import React from "react";
import {
  IconButton,
  Button,
  Badge,
  Flex,
  Spinner,
  Text,
  VStack,
  HStack,
  Box,
  Stack,
} from "@chakra-ui/react";
import { RiShareBoxLine } from "react-icons/ri";
import { useIntl } from "react-intl";
import DeleteButton from "src/components/DeleteModel";

import { MessageItemProps } from "./types";
import { useMessageContext } from "./MessageContext";
import {
  shouldShowAcceptDeclineActions,
  shouldShowShareAction,
  getBadgeColorScheme,
  stopPropagation,
} from "./utils";
import { STYLES, ACTION_BUTTON_CONFIGS } from "./constants";
import { MessageProvider } from "./MessageContext";
import useViewPortWidth from "src/utils/useViewPortWidth";

/**
 * Individual message item component with inline badge and external action buttons
 */
const MessageItem: React.FC<MessageItemProps> = ({
  intention,
  index,
  isSharedWithMe,
  isAccepted,
  onClick,
  onShare,
  remove,
  sharedId,
  approvalStatus,
  subliminal,
  senderData,
  createdAt,
}) => {
  const { formatMessage } = useIntl();
  const {
    handleAccept,
    handleDecline,
    handleDelete,
    isIntentionLoading,
    acceptedMessages,
  } = useMessageContext();

  // Get loading states for this specific intention
  const isAcceptingLoading = isIntentionLoading(intention.id, "accepting");
  const isDecliningLoading = isIntentionLoading(intention.id, "declining");
  const isDeletingLoading = isIntentionLoading(intention.id, "deleting");

  // Event handlers with proper event propagation control
  const handleAcceptClick = (e: React.MouseEvent) => {
    stopPropagation(e);
    if (!isIntentionLoading(intention.id) && sharedId) {
      handleAccept(sharedId, intention.id, intention.name);
    }
  };

  const handleDeclineClick = (e: React.MouseEvent) => {
    stopPropagation(e);
    if (!isIntentionLoading(intention.id) && sharedId) {
      handleDecline(sharedId, intention.id);
    }
  };

  const handleShareClick = (e: React.MouseEvent) => {
    stopPropagation(e);
    onShare?.(intention);
  };

  const handleDeleteClick = () => {
    if (!isIntentionLoading(intention.id)) {
      handleDelete(intention.id, Boolean(subliminal), sharedId, isSharedWithMe);
    }
  };

  const { viewPortWidth } = useViewPortWidth();

  // Render shared status badge as inline component
  const renderInlineSharedBadge = () => {
    if (!isSharedWithMe) return null;

    return (
      <Badge
        colorScheme={getBadgeColorScheme(isAccepted)}
        borderRadius={STYLES.BADGE.BORDER_RADIUS}
        px={STYLES.BADGE.HORIZONTAL_PADDING}
        py={STYLES.BADGE.VERTICAL_PADDING}
        fontSize={STYLES.BADGE.FONT_SIZE}
        fontFamily={STYLES.BADGE.FONT_FAMILY}
        fontWeight={STYLES.BADGE.FONT_WEIGHT}
        textTransform="none"
      >
        {isAccepted
          ? formatMessage({ id: "sharedAccepted" })
          : formatMessage({ id: "shared" })}
      </Badge>
    );
  };

  // Render Accept/Decline action buttons
  const renderAcceptDeclineActions = () => {
    if (
      !shouldShowAcceptDeclineActions(
        isSharedWithMe,
        approvalStatus,
        acceptedMessages,
        intention.id
      )
    )
      return null;

    const acceptButton = (
      <Button
        onClick={handleAcceptClick}
        onMouseDown={(e) => e.stopPropagation()}
        colorScheme="green"
        aria-label="Accept"
        size="sm"
        variant="solid"
        fontSize="xs"
        px={3}
        py={1}
        h="auto"
        minW="auto"
        isDisabled={isIntentionLoading(intention.id)}
      >
        {isAcceptingLoading ? (
          <Spinner size="sm" />
        ) : (
          formatMessage({ id: "acceptIntentionButton" })
        )}
      </Button>
    );
    const declineButton = (
      <Button
        onClick={handleDeclineClick}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label="Decline"
        colorScheme="red"
        size="sm"
        variant="solid"
        fontSize="xs"
        px={3}
        py={1}
        h="auto"
        minW="auto"
        isDisabled={isIntentionLoading(intention.id)}
      >
        {isDecliningLoading ? (
          <Spinner size="sm" />
        ) : (
          formatMessage({ id: "declineIntentionButton" })
        )}
      </Button>
    );

    return (
      <>
        {acceptButton}
        {declineButton}
      </>
    );
  };

  // Render Share action button
  const renderShareAction = () => {
    if (!shouldShowShareAction(isSharedWithMe) || !onShare) {
      return null;
    }

    return (
      <IconButton
        onClick={handleShareClick}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label="share"
        icon={<RiShareBoxLine className="remove-on-print" />}
        isDisabled={isIntentionLoading(intention.id)}
        {...ACTION_BUTTON_CONFIGS.SHARE}
      />
    );
  };

  // Render Delete action button
  const renderDeleteAction = () => {
    if (!remove) {
      return null;
    }

    if (isDeletingLoading) {
      return <Spinner size="sm" />;
    }

    return <DeleteButton remove={handleDeleteClick} />;
  };

  return (
    <MessageProvider>
      {/* @ts-ignore */}
      <Flex
        alignItems="center"
        justifyContent="start"
        width="100%"
        // maxWidth={STYLES.CONTAINER.MAX_WIDTH}
        height="auto"
        gap={STYLES.CONTAINER.GAP}
        opacity={isIntentionLoading(intention.id) ? 0.7 : 1}
      >
        {/* Box container mimicking Input styling */}
        <Box
          flex={1}
          position="relative"
          my={STYLES.INPUT.VERTICAL_MARGIN}
          className="message-Input"
          id={String(index)}
          onClick={(e) => {
            // Only trigger onClick if the click is not on a button
            const target = e.target as HTMLElement;
            const isButton = target.closest("button") !== null;
            if (!isButton && onClick) {
              // Create a synthetic event that matches what handleMessage expects
              const syntheticEvent = {
                target: {
                  value: intention.name,
                },
              };
              onClick(syntheticEvent);
            }
          }}
          onMouseDown={(e) => {
            // Completely prevent focus when clicking on buttons
            const target = e.target as HTMLElement;
            const isButton = target.closest("button") !== null;
            if (isButton) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          cursor="pointer"
          h="auto"
          py={STYLES.INPUT.VERTICAL_PADDING}
          px={4}
          borderRadius={STYLES.INPUT.BORDER_RADIUS}
          backgroundColor={STYLES.INPUT.BACKGROUND_COLOR}
          border="1px solid"
          borderColor="gray.200"
          opacity={isIntentionLoading(intention.id) ? 0.7 : 1}
          pointerEvents={isIntentionLoading(intention.id) ? "none" : "auto"}
          _hover={{
            borderColor: "gray.300",
            boxShadow: "sm",
          }}
          _focus={{
            borderColor: "blue.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
          }}
          _active={{
            borderColor: "blue.600",
            boxShadow: "0 0 0 1px var(--chakra-colors-blue-600)",
          }}
          tabIndex={0}
          role="button"
          aria-label={`Intention: ${intention.name}`}
        >
          {/* @ts-ignore */}
          <Stack
            justifyContent={
              viewPortWidth >= 425 ? "space-between" : "flex-start"
            }
            width="100%"
            direction={viewPortWidth >= 425 ? "row" : "column"}
          >
            <VStack alignItems="start" gap={0} flex={1}>
              <Text>{intention.name}</Text>
              {senderData && (
                <Text
                  fontSize="14px"
                  fontWeight="600"
                  color="gray.900"
                  lineHeight="1"
                >
                  {formatMessage({ id: "sharedBy" })}:{" "}
                  {senderData.roleId === 2
                    ? formatMessage({ id: "SmartsWay" })
                    : senderData.name}
                </Text>
              )}
              {senderData && (
                <Text fontSize="12px" color="gray.500">
                  {new Date(+senderData.sentAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Text>
              )}
              {!senderData && createdAt && (
                <Text fontSize="12px" color="gray.500">
                  {new Date(+createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Text>
              )}
            </VStack>

            <VStack
              alignItems="flex-end"
              justifyContent={senderData ? "center" : "flex-end"}
              gap={1}
              spacing={1}
              pr={0}
            >
              <HStack alignItems="center" gap={1} spacing={1} mb={1}>
                {renderInlineSharedBadge()}
                {renderShareAction()}
                {renderDeleteAction()}
              </HStack>
              {renderAcceptDeclineActions() && (
                <HStack alignItems="center" gap={1} spacing={1}>
                  {renderAcceptDeclineActions()}
                </HStack>
              )}
            </VStack>
          </Stack>
        </Box>
      </Flex>
    </MessageProvider>
  );
};

export default MessageItem;
